import { FormErrorAlert } from '@client/components/FormErrorAlert';
import { Button } from '@client/components/ui/button';
import { useAccountDetailForm } from '@client/hooks/useAccountDetailForm';
import { cn } from '@client/lib';
import { AppState } from '@client/model/store';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useEffect, useState } from 'react';
import { PiCrownSimple, PiWallet } from 'react-icons/pi';
import { useSelector } from 'react-redux';
import { AccountDetailFields } from './AccountDetailFields';
import { ASSET_OPTIONS } from './AssetSelectionGrid';

interface AccountDetailsAccordionProps {
  className?: string;
}

interface AccountDetailItemProps {
  asset: (typeof ASSET_OPTIONS)[number];
  isPriority: boolean;
  isExpanded: boolean;
  isComplete: boolean;
  onToggle: () => void;
  onComplete: () => void;
}

function AccountDetailItem({
  asset,
  isPriority,
  isExpanded,
  isComplete,
  onToggle,
  onComplete,
}: AccountDetailItemProps) {
  const {
    formState,
    formErrors,
    formHasBeenSubmitted,
    hasRequiredFieldErrors,
    isValid,
    handleChangeField,
    handleSubmit,
  } = useAccountDetailForm(asset.type);

  const handleDone = () => {
    const success = handleSubmit();
    if (success) {
      onComplete();
    }
  };

  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
      {/* Header */}
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center justify-between px-5 py-4 text-left transition-colors hover:bg-gray-50"
      >
        <div className="flex items-center gap-3">
          <div
            className={cn(
              'flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-gray-900',
            )}
          >
            {isPriority ? <PiCrownSimple className="h-5 w-5" /> : <PiWallet className="h-5 w-5" />}
          </div>
          <span className="font-semibold text-gray-900">{asset.label}</span>
        </div>

        <div className="flex items-center gap-3">
          {isPriority && (
            <span className="bg-primary/10 text-primary rounded-full px-3 py-1 text-xs font-medium">
              Priority
            </span>
          )}
          <span
            className={cn('rounded-full px-3 py-1 text-xs font-medium', {
              'bg-green-100 text-green-700': isComplete,
              'bg-amber-100 text-amber-700': !isComplete,
            })}
          >
            {isComplete ? 'Complete' : 'Incomplete'}
          </span>
          {isExpanded ? (
            <ChevronUp className="h-5 w-5 text-gray-400" />
          ) : (
            <ChevronDown className="h-5 w-5 text-gray-400" />
          )}
        </div>
      </button>

      {/* Content */}
      {isExpanded && (
        <div className="border-t border-gray-100 px-5 pb-5 pt-4">
          <AccountDetailFields
            formState={formState}
            formErrors={formErrors}
            onChangeField={handleChangeField}
          />

          {/* Error Summary */}
          {formHasBeenSubmitted && !isValid && (
            <FormErrorAlert
              className="mt-6"
              mainErrorMessage="There is an error with your submission"
            >
              {hasRequiredFieldErrors && (
                <li>
                  <span className="font-bold">Required field:</span> Please fill out all required
                  fields.
                </li>
              )}
            </FormErrorAlert>
          )}

          {/* Done Button */}
          <Button
            onClick={handleDone}
            className="mt-5 w-full"
          >
            Done
          </Button>
        </div>
      )}
    </div>
  );
}

export function AccountDetailsAccordion({ className }: AccountDetailsAccordionProps) {
  const { selectedAssets, priorityAssets, accountDetails } = useSelector(
    (state: { ltcFinancialPlanning: AppState['ltcFinancialPlanning'] }) =>
      state.ltcFinancialPlanning,
  );
  const [expandedAsset, setExpandedAsset] = useState<LtcFinancialPlanningAssetType | null>(
    selectedAssets[0] || null,
  );

  useEffect(() => {
    setExpandedAsset(selectedAssets[0] || null);
  }, [selectedAssets]);

  const availableAssets = ASSET_OPTIONS.filter(asset => selectedAssets.includes(asset.type));

  const handleToggle = (assetType: LtcFinancialPlanningAssetType) => {
    setExpandedAsset(expandedAsset === assetType ? null : assetType);
  };

  const handleComplete = (assetType: LtcFinancialPlanningAssetType) => {
    // Move to next asset
    const currentIndex = availableAssets.findIndex(a => a.type === assetType);
    if (currentIndex < availableAssets.length - 1) {
      setExpandedAsset(availableAssets[currentIndex + 1].type);
    } else {
      setExpandedAsset(null);
    }
  };

  return (
    <div className={cn('space-y-4', className)}>
      {availableAssets.map(asset => {
        const isPriority = priorityAssets.includes(asset.type);
        const isExpanded = expandedAsset === asset.type;
        const isComplete = accountDetails[asset.type]?.isComplete === true;

        return (
          <AccountDetailItem
            key={asset.type}
            asset={asset}
            isPriority={isPriority}
            isExpanded={isExpanded}
            isComplete={isComplete}
            onToggle={() => handleToggle(asset.type)}
            onComplete={() => handleComplete(asset.type)}
          />
        );
      })}
    </div>
  );
}
